import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { sortChatsByLastMessage } from "../../utility/chatUtils";
import { DeleteIcon, EditIconUnderLine, MoreIconFilled } from "../Icons/Icons";

export const ChatHistory = ({ chats, setChats, currentChatId, setCurrentChatId }) => {
  const VISIBLITY_COUNT = 20;
  const SIDEBAR_TITLE_LENGTH = 7;
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuChatId, setMenuChatId] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [chatToEdit, setChatToEdit] = useState(null);
  const [newTitle, setNewTitle] = useState("");

  const isMobile = useMediaQuery("(max-width: 809px)");

  const [visibleCount, setVisibleCount] = useState(VISIBLITY_COUNT);
  const containerRef = useRef(null);
  const throttleRef = useRef(false);

  const sortedChats = sortChatsByLastMessage(chats);

  const handleScroll = (e) => {
    if (throttleRef.current) return;
    throttleRef.current = true;

    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - VISIBLITY_COUNT) {
      setVisibleCount((prev) => Math.min(prev + VISIBLITY_COUNT, sortedChats.length));
    }

    setTimeout(() => {
      throttleRef.current = false;
    }, 200);
  };

  useEffect(() => {
    setVisibleCount(VISIBLITY_COUNT);
  }, [sortedChats]);

  const handleSelectChat = (chatId) => {
    setCurrentChatId(chatId);
  };

  const handleMenuOpen = (event, chatId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuChatId(chatId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuChatId(null);
  };

  const handleRename = () => {
    const chat = chats.find((c) => c.id === menuChatId);
    setChatToEdit(chat);
    setNewTitle(chat.title);
    setRenameOpen(true);
    handleMenuClose();
  };

  const handleRenameSave = () => {
    if (newTitle.trim()) {
      setChats((prevChats) =>
        prevChats.map((chat) => (chat.id === chatToEdit.id ? { ...chat, title: newTitle.trim() } : chat))
      );
    }
    setRenameOpen(false);
    setChatToEdit(null);
    setNewTitle("");
  };

  const handleDelete = () => {
    const chat = chats.find((c) => c.id === menuChatId);
    setChatToEdit(chat);
    setDeleteOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    setChats((prevChats) => {
      const updatedChats = prevChats.filter((chat) => chat.id !== chatToEdit.id);
      if (currentChatId === chatToEdit.id) {
        if (updatedChats.length > 0) {
          // Get the most recent chat after deletion
          const sortedRemainingChats = sortChatsByLastMessage(updatedChats);
          setCurrentChatId(sortedRemainingChats[0].id);
        } else {
          setCurrentChatId(null);
        }
      }
      return updatedChats;
    });
    setDeleteOpen(false);
    setChatToEdit(null);
  };

  const truncateTitle = (title) => {
    const words = title.split(" ");
    if (words.length <= SIDEBAR_TITLE_LENGTH) return title;
    return words.slice(0, SIDEBAR_TITLE_LENGTH).join(" ") + "...";
  };

  return (
    <>
      <Box className="chat-history-container" onScroll={handleScroll} ref={containerRef}>
        {sortedChats.slice(0, visibleCount).map((chat) => (
          <Box
            key={chat.id}
            className={`chat-history-item ${chat.id === currentChatId ? "selected" : ""}`}
            onClick={() => handleSelectChat(chat.id)}
          >
            <Typography className="chat-history-title" variant="body1" title={chat.title}>
              {truncateTitle(chat.title)}
            </Typography>

            <IconButton
              className={`chat-history-more-btn more-btn ${chat.id === menuChatId || isMobile ? "visible" : ""}`}
              onClick={(e) => handleMenuOpen(e, chat.id)}
            >
              <MoreIconFilled />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        elevation={1}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        className="chat-menu-popover"
      >
        <MenuList sx={{ padding: "0px" }}>
          <MenuItem
            onClick={handleRename}
            className="chat-menu-item"
            sx={{
              color: "success.main",
              "& .MuiSvgIcon-root": { color: "success.main" },
            }}
          >
            <EditIconUnderLine />
            Rename
          </MenuItem>
          <Divider sx={{ margin: "0px !important" }} />
          <MenuItem
            onClick={handleDelete}
            className="chat-menu-item delete"
            sx={{
              color: "error.main",
              "& .MuiSvgIcon-root": { color: "error.main" },
            }}
          >
            <DeleteIcon />
            Delete
          </MenuItem>
        </MenuList>
      </Popover>

      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)}>
        <DialogTitle>Rename Chat</DialogTitle>
        <DialogContent>
          <TextField value={newTitle} onChange={(e) => setNewTitle(e.target.value)} fullWidth autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleRenameSave}>Rename</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete chat "{chatToEdit?.title}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
